import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS, MDM_BASE_URL } from "../../../helpers/constants";
import apiInstance from "../../instances/apiInstance";
// VErCODE - CHECK THIS AND REVERT
import {
  isEmpty,
  isNotEmpty,
  validateApiParamValue,
} from "../../../helpers/utils";
import {
  AlternateDisplayNameApiResponse,
  AlternateDisplayNameQueryParams,
  DeleteCompatibleProgramTemplateParams,
  ProgramTemplateActivityParams,
  ProgramTemplateDeliverableParams,
} from "../client/models";
import { DropDownData, IdNameList } from "../common/models";
import {
  AddExistingDeliverableParams,
  CompatibleProgramTemplateListByCountryParams,
  CompatibleProgramTemplateListParams,
  CreateActivityParams,
  CreateCompatibleParams,
  CreateDeliverableParams,
  CreateProgramTemplateParams,
  GroupedLOBParams,
  ProgramClassParams,
  ProgramTemplateListParams,
  SearchProgramTemplateParams,
  SolutionCategoryLobParams,
  SolutionCategoryParams,
  templateListType,
  UpdateActivityParams,
} from "./models";

export const useGetLineOfBusiness = (languageCode?: string) => {
  return useQuery({
    queryKey: ["getLineOfBusiness", `${languageCode}`],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_LINE_OF_BUSINESS}?languageCode=${validateApiParamValue(languageCode)}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetProgramFrameWork = (
  languageCode?: string,
  type: string = "",
) => {
  return useQuery({
    queryKey: ["getProgramFrameWork", `${languageCode}`, `${type}`],
    queryFn: async (): Promise<IdNameList[]> => {
      let url = `${API_ENDPOINTS.GET_PROGRAM_FRAMEWORK}?languageCode=${languageCode}`;
      if (isNotEmpty(type)) {
        url += `&type=${type}`;
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
    retry: false,
  });
};

export const useSearchProgramTemplateMutation = () => {
  return useMutation({
    mutationFn: (params: SearchProgramTemplateParams) => {
      return apiInstance.post(API_ENDPOINTS.GET_PROGRAM_TEMPLATE, params);
    },
    retry: false,
  });
};

export const useGetProgramTemplateMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROGRAM_TEMPLATE_BY_ID,
      );
      return res.data;
    },
  });
};

export const useGetSolutionCategory = () => {
  const [params, setParams] = useState<SolutionCategoryParams>();
  const query = useQuery({
    queryKey: ["getSolutionCategory", params],
    queryFn: async (): Promise<DropDownData[]> => {
      if (!params) {
        return [];
      }
      const { languageCode = "", lineOfBusinessId = "" } = params;
      const languageCodeValue = validateApiParamValue(languageCode);
      const lineOfBusinessIdValue = validateApiParamValue(lineOfBusinessId);
      let url = `${API_ENDPOINTS.GET_SOLUTION_CATEGORY}?languageCode=${languageCodeValue}`;
      if (lineOfBusinessIdValue) {
        url += `&lineOfBusinessId=${lineOfBusinessIdValue}`;
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (newParams: SolutionCategoryParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetSolutionCategoryLob = () => {
  const [params, setParams] = useState<SolutionCategoryLobParams>();

  const query = useQuery({
    queryKey: ["getSolutionCategoryLob", params?.solutionCategoryId],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { languageCode = "", solutionCategoryId } = params;
      const languageCodeValue = validateApiParamValue(languageCode);
      const solutionCategoryIdValue = validateApiParamValue(solutionCategoryId);
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_SOLUTION_CATEGORY_LOB}?languageCode=${languageCodeValue}&solutionCategoryId=${solutionCategoryIdValue}`,
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: SolutionCategoryLobParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

// isGenericAccount
// true: will get solution offerings for generic account only
// false: will get solution offerings for all the account.
export const useGetSolutionOffering = (
  languageCode?: string,
  isGenericAccount: boolean = false,
  isParticipantTransfer: boolean = false,
) => {
  return useQuery({
    queryKey: ["getSolutionOffering", `${languageCode}`],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_SOLUTION_OFFERING}?languageCode=${languageCode}&isGenericDisplay=${isGenericAccount}&isParticipantTransfer=${isParticipantTransfer}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetProgramClass = () => {
  const [params, setParams] = useState<ProgramClassParams>();

  const query = useQuery({
    queryKey: ["getProgramClass", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }

      const languageCode = validateApiParamValue(params?.languageCode);
      const solutionOfferingId = validateApiParamValue(
        params?.solutionOfferingId,
      );

      if (isEmpty(solutionOfferingId) || isEmpty(languageCode)) {
        return [];
      }

      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_PROGRAM_CLASS}?languageCode=${languageCode}&solutionOfferingId=${solutionOfferingId}`,
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: ProgramClassParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useCreateProgramTemplateMutation = () => {
  return useMutation({
    mutationFn: (params: CreateProgramTemplateParams) => {
      return apiInstance.post(API_ENDPOINTS.POST_SAVE_PROGRAM_TEMPLATE, params);
    },
  });
};

export const useGetProgramTemplateById = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_BY_ID}/${id}`,
      );
      return res.data;
    },
  });
};

export const useUpdateProgramTemplateMutation = () => {
  return useMutation({
    mutationFn: (params: CreateProgramTemplateParams) => {
      return apiInstance.put(
        `${API_ENDPOINTS.UPDATE_PROGRAM_TEMPLATE}${params.id}`,
        params,
      );
    },
  });
};

export const useGetSearchActivitiesById = () => {
  const [programFrameWorkId, setProgramFrameWorkId] = useState<string>("");

  const query = useQuery({
    queryKey: ["getSearchActivitiesById", programFrameWorkId],
    queryFn: async () => {
      if (!programFrameWorkId) {
        return [];
      }
      const res = await apiInstance.get(
        `${MDM_BASE_URL}/programFrameworks/${validateApiParamValue(programFrameWorkId)}/activities`,
      );
      return res.data;
    },
    enabled: !!programFrameWorkId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setProgramFrameWorkId(newParams);
  };

  const clearSearchActivitiesData = () => {
    setProgramFrameWorkId("");
    // it will return [] array
  };

  return { ...query, refetchWithParams, clearSearchActivitiesData };
};

export const useGetActivityDeliverables = () => {
  return useQuery({
    queryKey: ["getActivityDeliverables"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_ACTIVITY_DELIVERABLES,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useCreateActivityMutation = () => {
  return useMutation({
    mutationFn: (params: CreateActivityParams) => {
      return apiInstance.post(API_ENDPOINTS.CREATE_ACTIVITY, params);
    },
  });
};

export const useGetListOfActivitiesById = () => {
  const [programTemplateId, setProgramTemplateId] = useState<string>("");
  const [programFrameWorkId, setProgramFrameWorkId] = useState<string>("");
  const query = useQuery({
    queryKey: [
      "getListOfActivitiesById",
      programTemplateId,
      programFrameWorkId,
    ],
    queryFn: async () => {
      if (!programTemplateId) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getListOfActivitiesById(programTemplateId ?? "", programFrameWorkId ?? "")}`,
      );
      return res.data;
    },
    enabled: !!programTemplateId && !!programFrameWorkId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (
    programTemplateId: string,
    programFrameWorkId: string,
  ) => {
    setProgramTemplateId(programTemplateId);
    setProgramFrameWorkId(programFrameWorkId);
  };

  const clearListOfActivitiesData = () => {
    setProgramTemplateId("");
    // it will return [] array
  };

  return { ...query, refetchWithParams, clearListOfActivitiesData };
};

export const useGetProgramTemplateActivityById = (id: string) => {
  return useQuery({
    queryKey: ["getProgramTemplateActivity", id],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_ACTIVITY_BY_ID}/${validateApiParamValue(id)}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useDeleteActivityFromTemplateMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: (params: ProgramTemplateActivityParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteActivityFromProgramTemplate(params.programTemplateId, params.activityId)}`,
      );
    },
  });
};

export const useDeleteDeliverableFromTemplateMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: (params: ProgramTemplateDeliverableParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteDeliverableFromProgramTemplate(params.programTemplateId, params.deliverableId)}`,
      );
    },
  });
};

export const useUpdateProgramTemplateActivityMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateActivityParams) => {
      return apiInstance.put(
        `${API_ENDPOINTS.updateActivityFromProgramTemplate(params.programTemplateId, params.activityId)}`,
        params,
      );
    },
  });
};

export const useAddNewDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      programTemplateId,
    }: {
      params: CreateDeliverableParams;
      programTemplateId: string;
    }) => {
      return apiInstance.post(
        API_ENDPOINTS.addNewDeliverable(programTemplateId ?? ""),
        params,
      );
    },
  });
};

export const useGetExistingDeliverablesById = (progTemplateId: string) => {
  const query = useQuery({
    queryKey: ["useGetExistingDeliverablesById", progTemplateId],
    queryFn: async () => {
      if (!progTemplateId) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.selectedDeliverable(progTemplateId ?? "")}`,
      );
      return res.data;
    },
    enabled: !!progTemplateId, // Only run when params are set
    retry: false,
  });

  const clearSearchActivitiesData = () => {
    // it will return [] array
  };

  return { ...query, clearSearchActivitiesData };
};

export const useAddExistingDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      programTemplateId,
    }: {
      params: AddExistingDeliverableParams;
      programTemplateId: string;
    }) => {
      return apiInstance.post(
        API_ENDPOINTS.selectedDeliverable(programTemplateId ?? ""),
        params,
      );
    },
  });
};

export const useGetProgramTemplateListForCreateProgram = (listType = "") => {
  const [params, setParams] = useState<ProgramTemplateListParams>();

  const query = useQuery({
    queryKey: ["getProgramClass", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      let templateApiUrl = `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_LIST_FOR_PROGRAM}`;
      let apiUrl = `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_LIST_FOR_PROGRAM}`;

      const {
        lobId = "",
        solutionOfferingsId = "",
        clientId = "",
        countryId = "",
      } = params;
      const lobIdValue = validateApiParamValue(lobId);
      const solutionOfferingsIdValue =
        validateApiParamValue(solutionOfferingsId);
      const clientIdValue = validateApiParamValue(clientId);
      const countryIdValue = validateApiParamValue(countryId);

      if (listType === templateListType.bulkTransfer) {
        apiUrl = `${templateApiUrl}?lobId=${lobIdValue}&solutionOfferingsId=${solutionOfferingsIdValue}`;
      } else {
        apiUrl = `${templateApiUrl}?clientId=${clientIdValue}&countryId=${countryIdValue}&lobId=${lobIdValue}`;
      }

      const res = await apiInstance.get(apiUrl);
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: ProgramTemplateListParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetProgramTemplateDeliverableById = (
  templateId: string,
  deliverableId: string,
) => {
  return useQuery({
    queryKey: ["getProgramTemplateDeliverableById", templateId, deliverableId],
    queryFn: async () => {
      if (!templateId || !deliverableId) return [];
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramTemplateDeliverablesById(
          deliverableId ?? "",
          templateId ?? "",
        ),
      );
      return res.data;
    },
    enabled: !!templateId && !!deliverableId, // Only run when both are present
    retry: false,
  });
};

export const useGetListOfDeliverables = () => {
  const [programTemplateId, setProgramTemplateId] = useState<string>("");
  const [programFrameWorkId, setProgramFrameWorkId] = useState<string>("");
  const query = useQuery({
    queryKey: ["getListOfDeliverables", programTemplateId, programFrameWorkId],
    queryFn: async () => {
      if (!programTemplateId) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getListOfDeliverables(programTemplateId ?? "", programFrameWorkId ?? "")}`,
      );
      return res.data;
    },
    enabled: !!programTemplateId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (
    programTemplateId: string,
    programFrameWorkId: string,
  ) => {
    setProgramTemplateId(programTemplateId);
    setProgramFrameWorkId(programFrameWorkId);
  };

  const clearListOfDeliverablesData = () => {
    setProgramTemplateId("");
  };

  return { ...query, refetchWithParams, clearListOfDeliverablesData };
};

export const useUpdateDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      programTemplateId,
      deliverableId,
    }: {
      params: CreateDeliverableParams;
      programTemplateId: string;
      deliverableId: string;
    }) => {
      return apiInstance.put(
        `${API_ENDPOINTS.updateDeliverable(programTemplateId ?? "", deliverableId ?? "")}`,
        params,
      );
    },
  });
};

export const useGetCompatibleTemplateListByCountry = () => {
  const [params, setParams] =
    useState<CompatibleProgramTemplateListByCountryParams>();

  const query = useQuery({
    queryKey: ["getCompatibleTemplateListByCountry", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }

      const { templateId, countryId } = params;

      const res = await apiInstance.get(
        API_ENDPOINTS.getCompatibleProgramTemplateListByCountry(
          templateId,
          countryId,
        ),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (
    newParams: CompatibleProgramTemplateListByCountryParams,
  ) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetCompatibleTemplateList = () => {
  const [params, setParams] = useState<CompatibleProgramTemplateListParams>();

  const query = useQuery({
    queryKey: ["getCompatibleTemplateList", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }

      const { templateId } = params;

      const res = await apiInstance.get(
        API_ENDPOINTS.getCompatibleProgramTemplateList(templateId),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (
    newParams: CompatibleProgramTemplateListParams,
  ) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useCreateCompatibleProgramTemplate = () => {
  return useMutation({
    mutationFn: ({
      params,
      templateId,
    }: {
      params: CreateCompatibleParams;
      templateId: string;
    }) => {
      return apiInstance.post(
        API_ENDPOINTS.addCompatibleProgramTemplate(templateId ?? ""),
        params,
      );
    },
  });
};

export const useDeleteCompatibleTemplateMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: (params: DeleteCompatibleProgramTemplateParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteCompatibleProgramTemplate(params.programTemplateId, params.compatibleProgramTemplateId)}`,
      );
    },
  });
};

export const useGetAllProgramTemplateForDropdown = () => {
  return useQuery({
    queryKey: ["getAllProgramTemplateForDropdown"],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_LIST_FOR_PROGRAM}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetAlternateDisplayName = () => {
  const [params, setParams] = useState<AlternateDisplayNameQueryParams>();

  const query = useQuery<AlternateDisplayNameApiResponse>({
    queryKey: ["getAlternateDisplayName", params],
    queryFn: async (): Promise<AlternateDisplayNameApiResponse> => {
      if (!params?.clientId) {
        return { name: "" };
      }
      const clientId = validateApiParamValue(params?.clientId);
      const programTemplateId = validateApiParamValue(
        params?.programTemplateId,
      );
      let url = `${API_ENDPOINTS.getAlternateDisplayName(clientId)}`;
      if (params.programTemplateId) {
        url += `?programTemplateId=${programTemplateId}`;
      }

      const res = await apiInstance.get(url);

      return { name: res.data };
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (newParams: AlternateDisplayNameQueryParams) => {
    setParams({ ...newParams });
  };

  return { ...query, refetchWithParams };
};

export const useGetProgramClosingReasonList = () => {
  return useQuery({
    queryKey: ["getProgramClosingReasonList"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROGRAM_CLOSING_REASON_LIST,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetProgramTemplateByLobId = () => {
  return useMutation({
    mutationFn: async (lobId: string): Promise<IdNameList[]> => {
      const queryParams = new URLSearchParams();
      queryParams.append("lobId", lobId);
      const queryString = queryParams.toString();

      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_PROGRAM_TEMPLATE_BY_LOB_ID}?${queryString}`,
      );
      return res.data;
    },
  });
};

// response will be having 2 keys lobWithBillingConfiguration & lobWithOutBillingConfiguration
export const useGetGroupedLineOfBusiness = () => {
  const [groupedParams, setGroupedParams] = useState<GroupedLOBParams>();

  const query = useQuery({
    queryKey: ["useGetGroupedLineOfBusiness", groupedParams],
    queryFn: async () => {
      if (!groupedParams) {
        return [];
      }

      let baseApiUrl = `${API_ENDPOINTS.GET_GROUPED_LINE_OF_BUSINESS}`;

      const { clientId = "" } = groupedParams;
      const clientIdValue = validateApiParamValue(clientId);

      const apiUrl = `${baseApiUrl}?clientId=${clientIdValue}`;

      const res = await apiInstance.get(apiUrl);
      return res.data;
    },
    enabled: !!groupedParams, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (groupedLobParams: GroupedLOBParams) => {
    setGroupedParams((prevParams) => ({
      ...groupedLobParams,
      isRefetch: !prevParams?.isRefetch,
    }));
  };

  return { ...query, refetchWithParams };
};

// response will be having 2 keys programTemplateWithBillingConfig & programTemplateWithOutBillingConfig
export const useGetGroupedProgramTemplateList = () => {
  const [groupedParams, setGroupedParams] =
    useState<ProgramTemplateListParams>();

  const query = useQuery({
    queryKey: ["useGetGroupedProgramTemplateList", groupedParams],
    queryFn: async () => {
      if (!groupedParams) {
        return [];
      }
      let templateApiUrl = `${API_ENDPOINTS.GET_GROUPED_PROGRAM_TEMPLATES}`;

      const {
        lobId = "",
        clientId = "",
        countryId = "",
        solutionOfferingsName = "",
      } = groupedParams;
      const lobIdValue = validateApiParamValue(lobId);
      const clientIdValue = validateApiParamValue(clientId);
      const countryIdValue = validateApiParamValue(countryId);
      const solutionOfferingsNameValue = validateApiParamValue(
        solutionOfferingsName,
      );

      let apiUrl = `${templateApiUrl}?clientId=${clientIdValue}&countryId=${countryIdValue}&lobId=${lobIdValue}`;

      if (solutionOfferingsNameValue) {
        apiUrl = `${apiUrl}&solutionOfferingsName=${solutionOfferingsNameValue}`;
      }

      const res = await apiInstance.get(apiUrl);
      return res.data;
    },
    enabled: !!groupedParams, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: ProgramTemplateListParams) => {
    setGroupedParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};
